import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAdmin } from "@/contexts/AdminContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Megaphone, X, Pencil, Trash2, Plus, Loader2, Tag, AlertTriangle, Info, Sparkles, ChevronDown,
} from "lucide-react";

const SEVERITY_ICON = {
  info: Info,
  promo: Sparkles,
  coupon: Tag,
  warning: AlertTriangle,
};

const SEVERITY_COLOR = {
  info: "var(--secondary)",
  promo: "var(--primary)",
  coupon: "#FFD60A",
  warning: "#FF3B30",
};

/**
 * BannerStack — fetches active banners scoped to the current view and
 * renders them at the top of the page. Each banner is dismissible (10/20/30
 * days or forever). Admins / group-owners / event-creators see edit + add
 * buttons inline.
 *
 * Props:
 *   scope: "global" | { group_id } | { event_id }  — controls add button visibility
 *   eventId / groupId — for fetching the right active list
 *   canManage: bool — does this user have edit power on the inline scope?
 */
export default function BannerStack({ groupId, eventId, scopeForAdd, canManage }) {
  const { user } = useAuth();
  const { unlocked, isAdminEmail } = useAdmin();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const adminEditOK = unlocked && isAdminEmail;

  const load = async () => {
    if (!user || !user.user_id) return;     // wait until auth resolves
    try {
      const params = new URLSearchParams();
      if (eventId) params.set("event_id", eventId);
      if (groupId) params.set("group_id", groupId);
      const { data } = await api.get(`/banners/active?${params.toString()}`);
      setItems(data || []);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoaded(true);
    }
  };

  // Re-fetch when auth resolves, scope changes, or admin unlocks
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [groupId, eventId, user?.user_id, adminEditOK]);

  const dismiss = async (b, days) => {
    try {
      await api.post(`/banners/${b.banner_id}/dismiss`, { snooze_days: days });
      setItems(items.filter((x) => x.banner_id !== b.banner_id));
    } catch (e) { setErr(formatError(e)); }
  };

  const del = async (b) => {
    if (!confirm(`Delete banner "${b.title}"?`)) return;
    try {
      await api.delete(`/banners/${b.banner_id}`);
      load();
    } catch (e) { setErr(formatError(e)); }
  };

  // Permission to edit a given banner: admin (for global) OR owner of group/event
  const canEditBanner = (b) => {
    if (b.scope === "global") return adminEditOK;
    return canManage; // group owner / event creator
  };

  const canAddHere = !!scopeForAdd && (
    scopeForAdd === "global" ? adminEditOK : canManage
  );

  return (
    <>
      {(items.length > 0 || canAddHere) && (
        <div className="flex flex-col gap-2" data-testid="banner-stack">
          {items.map((b) => (
            <BannerCard
              key={b.banner_id}
              banner={b}
              onDismiss={(days) => dismiss(b, days)}
              onEdit={canEditBanner(b) ? () => { setEditing(b); setEditorOpen(true); } : null}
              onDelete={canEditBanner(b) ? () => del(b) : null}
            />
          ))}
          {canAddHere && (
            <button
              className="bd-btn bd-btn-ghost self-start"
              style={{ fontSize: "0.8rem", padding: "0.4rem 0.7rem" }}
              onClick={() => { setEditing(null); setEditorOpen(true); }}
              data-testid="banner-add-btn"
            >
              <Plus size={12} /> Add a banner here
            </button>
          )}
        </div>
      )}
      {err && <div className="text-xs" style={{ color: "#ff3b30" }} data-testid="banner-err">{err}</div>}
      {editorOpen && (
        <BannerEditor
          scope={editing ? editing.scope : scopeForAdd}
          banner={editing}
          onClose={() => { setEditorOpen(false); setEditing(null); }}
          onSaved={() => { setEditorOpen(false); setEditing(null); load(); }}
        />
      )}
    </>
  );
}

function BannerCard({ banner, onDismiss, onEdit, onDelete }) {
  const [menu, setMenu] = useState(false);
  const Icon = SEVERITY_ICON[banner.severity] || Megaphone;
  const color = SEVERITY_COLOR[banner.severity] || "var(--primary)";

  return (
    <div
      className="bd-surface p-3 flex items-start gap-3"
      style={{ borderLeft: `4px solid ${color}` }}
      data-testid={`banner-${banner.banner_id}`}
    >
      <span
        className="w-9 h-9 grid place-items-center shrink-0 mt-0.5"
        style={{ background: "var(--bg-soft)", borderRadius: 9999, color }}
      >
        <Icon size={16} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <strong style={{ letterSpacing: "-0.01em" }}>{banner.title}</strong>
          {banner.scope === "global" && (
            <span className="bd-chip" style={{ fontSize: "0.65rem" }}>Global</span>
          )}
          {banner.coupon_code && (
            <span className="bd-chip bd-mono" style={{ fontSize: "0.7rem", background: "#FFD60A33", borderColor: "#FFD60A" }}>
              <Tag size={10} /> {banner.coupon_code}
            </span>
          )}
        </div>
        {banner.body && (
          <p className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>{banner.body}</p>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0 relative">
        {onEdit && (
          <button onClick={onEdit} className="bd-btn bd-btn-ghost" style={{ padding: "0.3rem" }} data-testid={`banner-edit-${banner.banner_id}`}>
            <Pencil size={12} />
          </button>
        )}
        {onDelete && (
          <button onClick={onDelete} className="bd-btn bd-btn-ghost" style={{ padding: "0.3rem" }} data-testid={`banner-delete-${banner.banner_id}`}>
            <Trash2 size={12} />
          </button>
        )}
        <button
          onClick={() => setMenu((m) => !m)}
          className="bd-btn bd-btn-ghost"
          style={{ padding: "0.3rem 0.45rem", fontSize: "0.7rem" }}
          data-testid={`banner-dismiss-${banner.banner_id}`}
        >
          <X size={12} /> <ChevronDown size={10} />
        </button>
        {menu && (
          <div
            className="absolute right-0 top-[110%] z-10 bd-surface flex flex-col"
            style={{ minWidth: 160, padding: "0.25rem" }}
            data-testid={`banner-dismiss-menu-${banner.banner_id}`}
          >
            {[
              { days: 10, label: "Snooze 10 days" },
              { days: 20, label: "Snooze 20 days" },
              { days: 30, label: "Snooze 30 days" },
              { days: 9999, label: "Don't show again" },
            ].map((opt) => (
              <button
                key={opt.days}
                onClick={() => { onDismiss(opt.days); setMenu(false); }}
                className="text-left px-3 py-1.5 rounded text-sm hover:opacity-80"
                style={{ background: "transparent" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-soft)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                data-testid={`banner-dismiss-${opt.days}-${banner.banner_id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BannerEditor({ scope, banner, onClose, onSaved }) {
  const [title, setTitle] = useState(banner?.title || "");
  const [body, setBody] = useState(banner?.body || "");
  const [severity, setSeverity] = useState(banner?.severity || "info");
  const [coupon, setCoupon] = useState(banner?.coupon_code || "");
  const [startAt, setStartAt] = useState(banner?.start_at ? banner.start_at.slice(0, 10) : "");
  const [endAt, setEndAt] = useState(banner?.end_at ? banner.end_at.slice(0, 10) : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (banner) {
        await api.patch(`/banners/${banner.banner_id}`, {
          title, body, severity,
          coupon_code: coupon || null,
          start_at: startAt || null,
          end_at: endAt || null,
        });
      } else {
        await api.post("/banners", {
          scope, title, body, severity,
          coupon_code: coupon || null,
          start_at: startAt || null,
          end_at: endAt || null,
        });
      }
      onSaved();
    } catch (e2) {
      setErr(formatError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose}>
      <div className="bd-surface p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="banner-editor">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold" style={{ letterSpacing: "-0.03em" }}>
            {banner ? "Edit banner" : "New banner"}
          </h2>
          <button onClick={onClose} className="bd-btn bd-btn-ghost" style={{ padding: "0.4rem" }}><X size={14} /></button>
        </div>
        <p className="text-xs mb-4 bd-mono" style={{ color: "var(--text-dim)" }}>Scope: {scope}</p>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="bd-label">Title</label>
            <input className="bd-input" required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="banner-title" />
          </div>
          <div>
            <label className="bd-label">Body (optional)</label>
            <textarea className="bd-input" rows={2} value={body} onChange={(e) => setBody(e.target.value)} data-testid="banner-body" />
          </div>
          <div>
            <label className="bd-label">Style</label>
            <div className="flex flex-wrap gap-2">
              {["info", "promo", "coupon", "warning"].map((s) => (
                <button
                  key={s} type="button"
                  className="bd-chip"
                  style={severity === s ? { background: "var(--primary)", color: "var(--primary-fg)", borderColor: "var(--primary)" } : {}}
                  onClick={() => setSeverity(s)}
                  data-testid={`banner-severity-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="bd-label">Coupon code label (optional, display only)</label>
            <input
              className="bd-input bd-mono"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value.toUpperCase())}
              placeholder="FAMILY10"
              data-testid="banner-coupon"
            />
            <p className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>
              For functional Stripe discounts, also create a coupon in the Admin → Coupons section with the same code.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="bd-label">Start date (optional)</label>
              <input className="bd-input" type="date" value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="banner-start" />
            </div>
            <div>
              <label className="bd-label">End date (optional)</label>
              <input className="bd-input" type="date" value={endAt} onChange={(e) => setEndAt(e.target.value)} data-testid="banner-end" />
            </div>
          </div>
          {err && <div className="text-sm" style={{ color: "#ff3b30" }} data-testid="banner-editor-err">{err}</div>}
          <button className="bd-btn w-full" type="submit" disabled={busy} data-testid="banner-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Save banner"}
          </button>
        </form>
      </div>
    </div>
  );
}
